/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { UserRole } from '@/const/roles'
import { useAuthStore } from '@/stores/useAuthStore.js'
import { createRouter, createWebHashHistory } from 'vue-router'

/**===================================== LAYOUT ===================================== */
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout.vue'

/**===================================== INTERCEPTORS ===================================== */
import RegisterInterceptor from './interceptor.js'
import { DOMAIN } from '@/const/domain.js'

/**===================================== AUTHENTICATION ===================================== */
const Login = () => import('@/features/auth/Login.vue')
const ForgotPassword = () => import('@/features/auth/ForgotPassword.vue')
const ResetPassword = () => import('@/features/auth/ResetPassword.vue')
const SSOCallbackHandler = () => import('@/features/auth/SSOCallbackHandler.vue')

/**===================================== DASHBOARD ===================================== */
const DashboardRouter = () => import('@/features/dashboard/DashboardRouter.vue')
const RFQListRouter = () => import('@/features/dashboard/RFQListRouter.vue')
const QCFListRouter = () => import('@/features/dashboard/QCFListRouter.vue')

/**===================================== CL PAGE ===================================== */
const QuotationSummaryCL = () => import('@/features/form/cl/action/QuotationSummaryCL.vue')
const PreviewQuotationSummaryCL = () =>
  import('@/features/form/cl/preview/PreviewQuotationSummaryCL.vue')

/**===================================== CS PAGE ===================================== */
const DeclinedItemsCS = () => import('@/features/form/cs/DeclinedItemsCS.vue')
const NotSubmittedItemsCS = () => import('@/features/form/cs/NotSubmittedItemsCS.vue')
const OERevisionCS = () => import('@/features/form/cs/OERevisionCS.vue')
const PreviewCSPage = () => import('@/features/form/cs/preview/PreviewCSPage.vue')
const PriceNotMatchCS = () => import('@/features/form/cs/PriceNotMatchCS.vue')
const SurrogateCSPage = () => import('@/features/form/cs/surrogate/SurrogateCSPage.vue')
const VendorApprovedPage = () => import('@/features/form/cs/VendorApprovedPage.vue')
const DICRequestRevisePage = () => import('@/features/form/cs/DICRequestRevise.vue')

/**===================================== USER/DIC PAGE ===================================== */
const ConfirmationItemDIC = () => import('@/features/form/dic/confirmation/ConfirmationItemDIC.vue')
const PreviewDICPage = () => import('@/features/form/dic/preview/PreviewDICPage.vue')
const SourcingDICPage = () => import('@/features/form/dic/sourcing/SourcingDICPage.vue')

/**===================================== VENDOR PAGE ===================================== */
const VendorQuotation = () => import('@/features/form/vendor/quotation/VendorQuotation.vue')
const PreviewVendorQuotation = () =>
  import('@/features/form/vendor/preview/PreviewVendorQuotation.vue')

/**===================================== ADMIN PAGE ===================================== */
const Settings = () => import('@/features/settings/SettingsPage.vue')

/**===================================== MASTER DATA PAGE ===================================== */
const MappingMaterial = () => import('@/features/MappingMaterial.vue')
const MasterUserPage = () => import('@/features/master-data/master-user/MasterUserPage.vue')
const UserMatrixPage = () => import('@/features/master-data/user-matrix/UserMatrixPage.vue')
const MasterDivisionsPage = () =>
  import('@/features/master-data/master-divisions/MasterDivisionsPage.vue')
const MaterialHierarchyPage = () =>
  import('@/features/master-data/material-hierarchy/MaterialHierarchyPage.vue')
const MaterialAssignmentPage = () =>
  import('@/features/master-data/material-assignment/MaterialAssignmentPage.vue')
const CategoriesPage = () => import('@/features/master-data/categories/CategoriesPage.vue')
const MatrixCategoryPage = () =>
  import('@/features/master-data/matrix-category/MatrixCategoryPage.vue')

/**===================================== STATUS PAGE ===================================== */
const PageExpired = () => import('@/features/expired/PageExpired.vue')
const ForbiddenPage = () => import('@/features/forbidden/ForbiddenPage.vue')
const SourcingSuccess = () => import('@/features/success/SourcingSuccess.vue')
const ContinueSourcingSuccess = () =>
  import('@/features/form/dic/sourcing/components/SuccessContinueSourcing.vue')
const SubmitSuccess = () => import('@/features/success/SubmitSuccess.vue')

export const publicPages = [
  'LoginPage',
  'ForgotPassword',
  'ResetPassword',
  'SSOCallback',
  'RegisterForm',
  'VendorQuotation',
  'ConfirmationItemDIC',
  'PageExpired',
  'SubmitSuccess',
  'SourcingSuccess',
]

/**
 * @type {import('vue-router').RouteRecordRaw[]}
 */
const routes = [
  /**
   * Public Routes
   * No Required Any Layout
   */
  // Auth
  {
    path: '/login',
    name: 'LoginPage',
    component: Login,
    meta: { domain: DOMAIN.AUTH },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { domain: DOMAIN.AUTH },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { domain: DOMAIN.AUTH },
  },
  {
    path: '/sso-callback',
    name: 'SSOCallback',
    component: SSOCallbackHandler,
    meta: { domain: DOMAIN.AUTH },
  },

  // Status pages
  {
    path: '/page-expired',
    name: 'PageExpired',
    component: PageExpired,
  },
  {
    path: '/page-forbidden',
    name: 'ForbiddenPage',
    component: ForbiddenPage,
  },
  {
    path: '/submit-success',
    name: 'SubmitSuccess',
    component: SubmitSuccess,
    meta: { fullscreen: true, domain: DOMAIN.RFQ },
  },
  {
    path: '/sourcing-success/:itemIds',
    name: 'SourcingSuccess',
    component: SourcingSuccess,
    meta: { fullscreen: true, domain: DOMAIN.RFQ },
  },
  {
    path: '/continue-sourcing-success/:rfq_number/:vendor_batch',
    name: 'ContinueSourcingSuccess',
    component: ContinueSourcingSuccess,
    meta: { domain: DOMAIN.RFQ },
  },

  // Vendor & DIC (public / token-based)
  {
    path: '/vendor/quotation/:token',
    name: 'VendorQuotation',
    component: VendorQuotation,
    meta: {
      domain: DOMAIN.RFQ,
      redirect401: 'page-expired',
    },
  },
  {
    path: '/confirmation-item-dic/:token',
    name: 'ConfirmationItemDIC',
    component: ConfirmationItemDIC,
    meta: {
      domain: DOMAIN.RFQ,
      redirect401: 'page-expired',
    },
  },

  /**
   * Authenticated Routes
   */
  {
    path: '/',
    component: AuthenticatedLayout,
    children: [
      // General
      {
        path: 'dashboard',
        name: 'DashboardRouter',
        component: DashboardRouter,
      },
      {
        path: 'rfqlist',
        name: 'RFQListRouter',
        component: RFQListRouter,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'qcflist',
        name: 'QCFListRouter',
        component: QCFListRouter,
        meta: {
          domain: DOMAIN.QCF,
          roles: [UserRole.ADMIN, UserRole.CL, UserRole.MANAGEMENT],
        },
      },

      // ADMIN ROUTES
      {
        path: 'settings',
        name: 'Settings',
        component: Settings,
        meta: {
          domain: DOMAIN.SETTINGS,
          roles: [UserRole.ADMIN],
        },
      },
      {
        path: 'mapping-material',
        name: 'MappingMaterial',
        component: MappingMaterial,
        meta: {
          domain: DOMAIN.MAPPING_MATERIAL,
          roles: [UserRole.ADMIN],
        },
      },

      // CS ROUTES (NON TOKEN)
      {
        path: 'cs/declined-items/:rfq_number/:vendor_batch',
        name: 'DeclinedItemsCS',
        component: DeclinedItemsCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'cs/not-submitted-items/:rfq_number/:vendor_batch',
        name: 'NotSubmittedItemsCS',
        component: NotSubmittedItemsCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'cs/preview/:rfq_number/:vendor_batch',
        name: 'PreviewCSPage',
        component: PreviewCSPage,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'cs/surrogate-items/:rfq_number/:vendor_batch',
        name: 'SurrogateCSPage',
        component: SurrogateCSPage,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'cs/vendor-approved/:rfq_number/:vendor_batch',
        name: 'VendorApprovedPage',
        component: VendorApprovedPage,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'cs/dic-request-revise/:rfq_number/:vendor_batch',
        name: 'DICRequestRevisePage',
        component: DICRequestRevisePage,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'cs/oe-revision/:rfq_number/:vendor_batch',
        name: 'OERevisionCS',
        component: OERevisionCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },
      {
        path: 'cs/price-not-match/:rfq_number/:vendor_batch',
        name: 'PriceNotMatchCS',
        component: PriceNotMatchCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
        },
      },

      // CS ROUTES (TOKEN)
      {
        path: 'cs/declined-items/token/:token',
        name: 'DeclinedItemsCSToken',
        component: DeclinedItemsCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
          redirect401: 'page-expired',
        },
      },
      {
        path: 'cs/not-submitted-items/token/:token',
        name: 'NotSubmittedItemsCSToken',
        component: NotSubmittedItemsCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
          redirect401: 'page-expired',
        },
      },
      {
        path: 'cs/oe-revision/token/:token',
        name: 'OERevisionCSToken',
        component: OERevisionCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
          redirect401: 'page-expired',
        },
      },
      {
        path: 'cs/price-not-match/token/:token',
        name: 'PriceNotMatchCSToken',
        component: PriceNotMatchCS,
        meta: {
          domain: DOMAIN.RFQ,
          roles: [UserRole.ADMIN, UserRole.CS],
          redirect401: 'page-expired',
        },
      },

      // CL ROUTES
      {
        path: 'quotation-summary/:qcf_number',
        name: 'QuotationSummaryCL',
        component: QuotationSummaryCL,
        meta: {
          domain: DOMAIN.QCF,
          roles: [UserRole.ADMIN, UserRole.CL, UserRole.MANAGEMENT],
        },
      },
      {
        path: 'preview-quotation-summary/:qcf_number',
        name: 'PreviewQuotationSummaryCL',
        component: PreviewQuotationSummaryCL,
        meta: {
          domain: DOMAIN.QCF,
          roles: [UserRole.ADMIN, UserRole.CL, UserRole.MANAGEMENT],
        },
      },
      {
        path: 'quotation-summary/token/:token',
        name: 'QuotationSummaryCLToken',
        component: QuotationSummaryCL,
        meta: {
          domain: DOMAIN.QCF,
          roles: [UserRole.ADMIN, UserRole.CL, UserRole.MANAGEMENT],
          redirect401: 'page-expired',
        },
      },

      // DIC ROUTES
      {
        path: '/preview-item-dic/:rfq_number',
        name: 'PreviewDICPage',
        component: PreviewDICPage,
        meta: {
          domain: DOMAIN.RFQ,
        },
      },
      {
        path: '/sourcing-item-dic/:rfq_number/:milestone_id',
        name: 'SourcingDICPage',
        component: SourcingDICPage,
        meta: {
          domain: DOMAIN.RFQ,
        },
      },

      // VENDOR ROUTES
      {
        path: '/preview-vendor/:vendor_code/:rfq_number/:vendor_batch',
        name: 'PreviewVendorQuotation',
        component: PreviewVendorQuotation,
        meta: {
          domain: DOMAIN.RFQ,
        },
      },

      // MASTER DATA
      {
        path: 'master-data/master-user',
        name: 'MasterUserPage',
        component: MasterUserPage,
        meta: {
          domain: DOMAIN.MASTER_USER,
          roles: [UserRole.ADMIN],
        },
      },
      {
        path: 'master-data/user-matrix',
        name: 'UserMatrixPage',
        component: UserMatrixPage,
        meta: {
          domain: DOMAIN.USER_MATRIX,
          roles: [UserRole.ADMIN],
        },
      },
      {
        path: 'master-data/master-divisions',
        name: 'MasterDivisionsPage',
        component: MasterDivisionsPage,
        meta: {
          domain: DOMAIN.MASTER_DIVISION,
          roles: [UserRole.ADMIN],
        },
      },
      {
        path: 'master-data/material-hierarchy',
        name: 'MaterialHierarchyPage',
        component: MaterialHierarchyPage,
        meta: {
          domain: DOMAIN.MATERIAL_HIERARCHY,
          roles: [UserRole.ADMIN],
        },
      },
      {
        path: 'master-data/material-assignment',
        name: 'MaterialAssignmentPage',
        component: MaterialAssignmentPage,
        meta: {
          domain: DOMAIN.MATERIAL_ASSIGNMENT,
          roles: [UserRole.ADMIN],
        },
      },
      {
        path: 'master-data/categories',
        name: 'MasterCategoriesPage',
        component: CategoriesPage,
        meta: {
          domain: DOMAIN.CATEGORY,
          roles: [UserRole.ADMIN],
        },
      },
      {
        path: 'master-data/matrix-category',
        name: 'MatrixCategoryPage',
        component: MatrixCategoryPage,
        meta: {
          domain: DOMAIN.MATRIX_CATEGORY,
          roles: [UserRole.ADMIN],
        },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, from, next) => {
  if (to.path === '/reset-password.html') {
    return next({ path: '/reset-password', query: to.query, replace: true })
  }

  const authRequired = !publicPages.includes(to.name)
  const auth = useAuthStore()

  const defaultByRole = {
    [UserRole.ADMIN]: '/dashboard', // Admin
    [UserRole.CL]: '/dashboard', // CL
    [UserRole.CS]: '/dashboard', // CS
    [UserRole.USER]: '/dashboard', // User
    [UserRole.MANAGEMENT]: '/dashboard', // Management
    [UserRole.VENDOR]: '/dashboard', // Vendor
  }

  // Super Admin shortcut
  if (to.meta.isSuperAdmin && auth.userRole == UserRole.ADMIN && auth.isAuthenticated) {
    return next()
  }

  // Not authenticated
  if (authRequired && !auth.isAuthenticated) {
    auth.returnUrl = to.fullPath
    return next('/login')
  }

  // Authenticated user accessing root path → redirect based on role
  if (auth.isAuthenticated && to.path === '/' && !to.name) {
    const destination = defaultByRole[auth.userRole] || '/dashboard'
    return next(destination)
  }

  // Role mismatch → gunakan fallback berdasarkan role
  if (to.meta?.roles && !to.meta.roles.includes(auth.userRole)) {
    // Redirecting silently makes a shared/deep link look like it simply did
    // nothing. Send the user to the forbidden page so the refusal is visible.
    return next('/page-forbidden')
  }

  // Authenticated mengakses public page (non-token)
  if (!authRequired && auth.isAuthenticated) {
    if (
      to.name === 'VendorQuotation' ||
      to.name === 'ConfirmationItemDIC' ||
      to.name === 'ResetPassword'
    ) {
      return next()
    }

    // redirect ke default page sesuai role
    const go = defaultByRole[auth.userRole] || '/dashboard'
    return next(go)
  }

  // Default case
  next()
})

router.afterEach(to => {
  const auth = useAuthStore()
  if (auth.isAuthenticated && to.name !== 'Login') {
    auth.lastVisited = to.fullPath
  }
})

router.onError((err, to) => {
  const isChunkLoadError =
    err?.message?.includes('Failed to fetch dynamically imported module') ||
    err?.message?.includes('error loading dynamically imported module') ||
    err?.message?.includes('Importing a module script failed') ||
    err?.name === 'ChunkLoadError'

  if (isChunkLoadError) {
    err.handled = true

    // prevent loop reload
    const reloadKey = `chunk_reload_${to?.name}`
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1')
      globalThis.location.replace(to?.fullPath ?? '/')
    }
  } else {
    console.error('Router error:', err)
  }
})

// always at the end
RegisterInterceptor(router)

export default router
